import React, { useState, useContext, useEffect } from "react";
import { Table, Button } from "reactstrap";
import { FileMinus } from "lucide-react";
import { AppContext } from '../application/provider';
import Loader from "../components/Loader/Loader";
import { ButtonSearch } from '../components/Buttons/Buttons';
import ButtonPdf from '../components/Buttons/ButtonPdf';
import ButtonExcel from '../components/Buttons/ButtonExcel';
import ButtonInsert from '../components/Buttons/ButtonInsert';
import "./CargoPago.css";

import PlanillaNegociosService from "../axios_services/planilla_negocios.service";
import ArchivosService from "../axios_services/archivos.service";

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const getEmptyRow = () => ({
    planilla_negocios_id: null, area: 'NEGOCIOS', apellidos_nombre: '',
    cargo: '', remuneracion: 0, bono_anual: 0, descuento_tardanza_faltas: 0,
    total_pagar_planilla: 0, total_aporte_trabajador: 0, importe_neto_pagar: 0,
    dni: '', n_cuenta: '', banco: '', fecha_pago: null, boleta_url: null
});

function PlanillaNegocios() {
    const [state] = useContext(AppContext);
    const [periodo_fecha, setPeriodoFecha] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [loading, setLoading] = useState(true);
    const [planillaData, setPlanillaData] = useState([]);
    const [validationStatus, setValidationStatus] = useState(null);

    const fetchDatos = async () => {
        setLoading(true);
        try {
            const dbPlanilla = await PlanillaNegociosService.getPlanilla(periodo_fecha).catch(() => []);
            const arrDbPlanilla = Array.isArray(dbPlanilla) ? dbPlanilla : (dbPlanilla?.data || Object.values(dbPlanilla || {}) || []);

            const boletasData = await ArchivosService.getBoletasPagoPeriodo(periodo_fecha).catch(() => []);
            const arrBoletas = Array.isArray(boletasData) ? boletasData : (boletasData?.data || []);

            const validationRow = arrDbPlanilla.find(p => p && (p.aprobado_th || p.aprobado_sa || p.aprobado_gg)) || arrDbPlanilla[0];
            const isThAprob = validationRow ? validationRow.aprobado_th : 0;
            const isSaAprob = validationRow ? validationRow.aprobado_sa : 0;
            const isGgAprob = validationRow ? validationRow.aprobado_gg : 0;

            const computedData = arrDbPlanilla.map((dbRow) => {
                const rowData = {
                    ...dbRow,
                    area: dbRow.area || '',
                    apellidos_nombre: dbRow.apellidos_nombre || '',
                    cargo: dbRow.cargo || '',
                    remuneracion: parseFloat(dbRow.remuneracion || 0),
                    bono_anual: parseFloat(dbRow.bono_anual || 0),
                    descuento_tardanza_faltas: parseFloat(dbRow.descuento_tardanza_faltas || 0),
                    total_pagar_planilla: parseFloat(dbRow.total_pagar_planilla || 0),
                    total_aporte_trabajador: parseFloat(dbRow.total_aporte_trabajador || 0),
                    importe_neto_pagar: parseFloat(dbRow.importe_neto_pagar || 0),
                    dni: dbRow.dni || '',
                    n_cuenta: dbRow.n_cuenta || '',
                    banco: dbRow.banco || '',
                };

                const boleta = arrBoletas.find(b => 
                    (Number(b.origen_id) === Number(dbRow.planilla_negocios_id)) || 
                    (Number(b.usuario_id) === Number(dbRow.planilla_negocios_id))
                );
                rowData.boleta_url = boleta ? boleta.url_completa : null;
                rowData.fecha_pago_db = rowData.fecha_pago;

                return rowData;
            });

            if (computedData.length > 0) {
                setPlanillaData(computedData);
                setValidationStatus({
                    aprobado_th: isThAprob,
                    aprobado_sa: isSaAprob,
                    aprobado_gg: isGgAprob
                });
            } else {
                setPlanillaData([]);
                setValidationStatus(null);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
            setPlanillaData([]);
            setValidationStatus(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDatos();
    }, [periodo_fecha]);

    const handleSearch = () => {
        fetchDatos();
    };

    const handleAddRow = () => {
        const newRow = getEmptyRow();
        setPlanillaData([...planillaData, newRow]);
    };

    const handleInputChange = (e, index, field) => {
        const val = e.target.value;
        const newData = [...planillaData];
        const numericFields = ['remuneracion', 'bono_anual', 'descuento_tardanza_faltas', 'total_pagar_planilla', 'total_aporte_trabajador', 'importe_neto_pagar'];

        if (numericFields.includes(field)) {
            newData[index][field] = parseFloat(val) || 0;

            const row = newData[index];
            if (field === 'remuneracion' || field === 'bono_anual' || field === 'descuento_tardanza_faltas') {
                row.total_pagar_planilla = (row.remuneracion + row.bono_anual) - row.descuento_tardanza_faltas;
            }
            if (['remuneracion', 'bono_anual', 'descuento_tardanza_faltas', 'total_pagar_planilla', 'total_aporte_trabajador'].includes(field)) {
                row.importe_neto_pagar = row.total_pagar_planilla - row.total_aporte_trabajador;
            }

        } else {
            newData[index][field] = val;
        }

        setPlanillaData(newData);
    };

    const handleAprobar = async () => {
        if (!window.confirm("¿Está seguro de que desea aprobar y firmar la Planilla Negocios? Se registrarán todos los datos manuales actuales.")) {
            return;
        }
        setLoading(true);
        try {
            const payload = {
                periodo_planilla: periodo_fecha,
                registros: planillaData.map(d => ({
                    planilla_negocios_id: d.planilla_negocios_id,
                    area: d.area,
                    apellidos_nombre: d.apellidos_nombre,
                    cargo: d.cargo,
                    remuneracion: d.remuneracion,
                    bono_anual: d.bono_anual,
                    descuento_tardanza_faltas: d.descuento_tardanza_faltas,
                    total_pagar_planilla: d.total_pagar_planilla,
                    total_aporte_trabajador: d.total_aporte_trabajador,
                    importe_neto_pagar: d.importe_neto_pagar,
                    dni: d.dni,
                    n_cuenta: d.n_cuenta,
                    banco: d.banco,
                    fecha_pago: d.fecha_pago
                }))
            };
            await PlanillaNegociosService.guardarPlanilla(payload);

            const perfilId = Number(state.user.perfil_id);
            await PlanillaNegociosService.validarPlanilla(periodo_fecha, perfilId);

            alert("Planilla Negocios aprobada correctamente.");
            await fetchDatos();
        } catch (error) {
            alert("Error al procesar la aprobación: " + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const formatSoles = (val) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(val || 0);
    const userId = Number(state.user?.perfil_id);

    // Perfil 11 registra, perfil 5 y 8 recursos, 1 o 26 admin/subgerencia, 9 gerencia
    const canEdit = [11, 5, 8, 26, 9].includes(userId) && Number(validationStatus?.aprobado_th) !== 1;
    const canEditPaymentDate = [5, 8, 26].includes(userId) && Number(validationStatus?.aprobado_th) === 1; // 5 registers it after basic validation
    const canUploadBoleta = [5, 8, 11, 26].includes(userId);
    const totalImporteNeto = planillaData.reduce((sum, row) => sum + (Number(row.importe_neto_pagar) || 0), 0);


    const getStatusText = () => {
        if (!validationStatus) return "PENDIENTE";
        if (Number(validationStatus.aprobado_gg) === 1) return "APROBADO POR GERENCIA GENERAL";
        if (Number(validationStatus.aprobado_sa) === 1) return "APROBADO POR GERENCIA DE NEGOCIOS";
        if (Number(validationStatus.aprobado_th) === 1) return "APROBADO POR TALENTO HUMANO";
        return "PENDIENTE";
    };

    const isApprovalDisabled = () => {
        if (!state.user) return true;
        const perfil = Number(state.user.perfil_id);
        const { aprobado_th, aprobado_sa, aprobado_gg } = validationStatus || {};

        if ([5, 8].includes(perfil)) return Number(aprobado_th) === 1;
        if ([1, 26].includes(perfil)) return Number(aprobado_th) !== 1 || Number(aprobado_sa) === 1;
        if (perfil === 9) return Number(aprobado_sa) !== 1 || Number(aprobado_gg) === 1;
        // Profil 11 can save, but may not necessarily advance the approval flow. We allow them to save at least.
        if (perfil === 11) return false;
        return true;
    };

    const isExportEnabled = Number(validationStatus?.aprobado_gg) === 1;

    const exportToPDF = async () => {
        const doc = new jsPDF('landscape', 'pt', 'a4');
        const getMonthName = (YYYY_MM) => {
            if (!YYYY_MM) return '';
            const [y, m] = YYYY_MM.split('-');
            const date = new Date(Number(y), Number(m) - 1, 1);
            return date.toLocaleString('es-PE', { month: 'long', year: 'numeric' }).toUpperCase();
        };
        const monthYearStr = getMonthName(periodo_fecha);

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("MEC BUSINNES PARTNER S.A.C.", 28, 30);
        doc.text("20612796484", 28, 45);

        const tableColumnNames = [
            "N°", "AREA", "APELLIDOS Y NOMBRES", "CARGO", "REMUNERACIÓN", "BONO ANUAL",
            { content: "DESCUENTO POR TARDANZA O FALTAS", styles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] } },
            "TOTAL A PAGAR PLANILLA", "TOTAL APORT. DEL TRABAJADOR",
            { content: "IMPORTE NETO A PAGAR", styles: { fillColor: [255, 255, 0], textColor: [0, 0, 0] } },
            "N° DNI", "N° CUENTA", "BANCO",
            { content: "FECHA DE PAGO", styles: { fillColor: [225, 213, 231], textColor: [0, 0, 0] } }
        ];

        const formatNumber = (val) => {
            if (val === 0 || !val) return "S/ -";
            return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 }).format(val);
        };

        const tableRows = planillaData.map((row, index) => [
            index + 1,
            row.area?.toUpperCase(),
            row.apellidos_nombre?.toUpperCase(),
            row.cargo?.toUpperCase(),
            formatNumber(row.remuneracion),
            formatNumber(row.bono_anual),
            formatNumber(row.descuento_tardanza_faltas),
            formatNumber(row.total_pagar_planilla),
            formatNumber(row.total_aporte_trabajador),
            { content: formatNumber(row.importe_neto_pagar), styles: { fillColor: [255, 255, 204], textColor: [0, 0, 0], fontStyle: 'bold' } },
            row.dni,
            row.n_cuenta,
            row.banco?.toUpperCase(),
            row.fecha_pago || ""
        ]);

        const totalRow = Array(14).fill("");
        totalRow[9] = formatNumber(totalImporteNeto);
        tableRows.push(totalRow);

        autoTable(doc, {
            startY: 60,
            head: [
                [{
                    content: `PLANILLA NEGOCIOS ${monthYearStr}`,
                    colSpan: 14,
                    styles: { halign: 'center', fillColor: [255, 192, 0], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 9, lineColor: [0, 0, 0], lineWidth: 0.5 }
                }],
                tableColumnNames
            ],
            body: tableRows,
            theme: 'grid',
            styles: { fontSize: 5, cellPadding: 1.5, overflow: 'linebreak', halign: 'center', valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.5 },
            margin: { left: 20, right: 20 },
            headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 5, lineColor: [0, 0, 0], lineWidth: 0.5 },
            columnStyles: {
                2: { halign: 'left', cellWidth: 70 },
                1: { cellWidth: 35 },
            },
            didParseCell: function (data) {
                if (data.section === 'body' && data.row.index === tableRows.length - 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [255, 255, 255];
                }
            }
        });

        const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 100;
        let startY = finalY + 110;
        if (startY > doc.internal.pageSize.height - 70) { doc.addPage(); startY = 70; }

        try {
            /*
            // =========================================================================
            // CODIGO ANTERIOR (CON TALENTO HUMANO / PERFIL 5):
            // Descomentar cuando vuelva el perfil 5 con jefatura independiente:
            // =========================================================================
            // const [firmaThRes, firmaGgRes, firmaSaRes] = await Promise.all([
            //     ArchivosService.getFirmaDigitalConvenio(88).catch(() => null),
            //     ArchivosService.getFirmaDigitalConvenio(132).catch(() => null),
            //     ArchivosService.getFirmaDigitalConvenio(131).catch(() => null)
            // ]);
            // const firmaThB64 = firmaThRes?.base64 || null;
            // const firmaGgB64 = firmaGgRes?.base64 || null;
            // const firmaSaB64 = firmaSaRes?.base64 || null;
            // const pageWidth = doc.internal.pageSize.width;
            // const center1 = pageWidth * 0.25; const center2 = pageWidth * 0.50; const center3 = pageWidth * 0.75;
            // drawSignature(firmaGgB64, "RAMIREZ TAIPE MANUEL ANGEL", "GERENTE GENERAL", center1, startY);
            // drawSignature(firmaSaB64, "RISSO CISNEROS ERIK JACKSON", "GERENTE DE NEGOCIOS", center2, startY);
            // drawSignature(firmaThB64, "CHIPANA GAGO FLERIDA DANIELA", "JEFE DE TALENTO HUMANO", center3, startY);
            // =========================================================================
            */

            // CONFIGURACIÓN ACTUAL (3 FIRMAS: SUBGERENCIA DE ADMINISTRACIÓN, GERENCIA DE NEGOCIOS, GERENCIA GENERAL)
            const [firmaAdminRes, firmaGnRes, firmaGgRes] = await Promise.all([
                ArchivosService.getFirmaDigitalConvenio(251).catch(() => null),
                ArchivosService.getFirmaDigitalConvenio(131).catch(() => null),
                ArchivosService.getFirmaDigitalConvenio(132).catch(() => null)
            ]);
            const firmaAdminB64 = firmaAdminRes?.base64 || null;
            const firmaGnB64 = firmaGnRes?.base64 || null;
            const firmaGgB64 = firmaGgRes?.base64 || null;
            const pageWidth = doc.internal.pageSize.width;
            const center1 = pageWidth * 0.20; const center2 = pageWidth * 0.50; const center3 = pageWidth * 0.80;

            doc.setFontSize(7);
            const drawSignature = (b64, name, role, cx, y) => {
                if (b64) {
                    const format = b64.includes('image/jpeg') || b64.includes('image/jpg') || b64.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
                    doc.addImage(b64, format, cx - 50, y - 50, 100, 50);
                }
                doc.setLineWidth(0.5);
                doc.line(cx - 75, y, cx + 75, y);
                doc.setFont("helvetica", "bold"); doc.text(role, cx, y + 12, { align: 'center' });
                doc.setFont("helvetica", "normal"); doc.text(name, cx, y + 22, { align: 'center' });
            };

            drawSignature(firmaAdminB64, "ROSALES SAMANIEGO GABRIELA PATRICIA", "SUB GERENTE DE ADMINISTRACIÓN", center1, startY);
            drawSignature(firmaGnB64, "RISSO CISNEROS ERIK JACKSON", "GERENTE DE NEGOCIOS", center2, startY);
            drawSignature(firmaGgB64, "RAMIREZ TAIPE MANUEL ANGEL", "GERENTE GENERAL", center3, startY);
        } catch (e) { }

        doc.save(`planilla_negocios_${periodo_fecha.replace('-', '_')}.pdf`);
    };

    const exportToExcel = async () => {
        const getMonthName = (YYYY_MM) => {
            if (!YYYY_MM) return '';
            const [y, m] = YYYY_MM.split('-');
            const date = new Date(Number(y), Number(m) - 1, 1);
            return date.toLocaleString('es-PE', { month: 'long', year: 'numeric' }).toUpperCase();
        };
        const monthYearStr = getMonthName(periodo_fecha);

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Planilla Negocios');

        worksheet.getRow(1).values = ['MEC BUSINNES PARTNER S.A.C.'];
        worksheet.getRow(2).values = ['20612796484'];
        worksheet.mergeCells('A4:N4');
        worksheet.getCell('A4').value = `PLANILLA NEGOCIOS DE ${monthYearStr}`;
        worksheet.getCell('A4').alignment = { horizontal: 'center' };

        const headers = [
            "N°", "AREA", "APELLIDOS Y NOMBRES", "CARGO", "REMUNERACIÓN", "BONO ANUAL", "DESCUENTO POR TARDANZA O FALTAS",
            "TOTAL A PAGAR PLANILLA", "TOTAL APORT. DEL TRABAJADOR", "IMPORTE NETO A PAGAR",
            "N° DNI", "N° CUENTA", "BANCO", "FECHA DE PAGO"
        ];
        worksheet.getRow(5).values = headers;
        worksheet.getRow(5).font = { bold: true };

        planillaData.forEach((row, index) => {
            worksheet.addRow([
                index + 1, row.area, row.apellidos_nombre, row.cargo, row.remuneracion, row.bono_anual, row.descuento_tardanza_faltas,
                row.total_pagar_planilla, row.total_aporte_trabajador, row.importe_neto_pagar,
                row.dni, row.n_cuenta, row.banco, row.fecha_pago
            ]);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `Planilla_Negocios_${periodo_fecha}.xlsx`);
    };

    return (
        <div className="cargo-pago-module">
            {loading && <Loader />}
            <div className="flex items-center justify-between mb-4">
                <h1>CARGO DE PAGO - PLANILLA NEGOCIOS</h1>
                <div className="flex gap-2">
                    <ButtonPdf onClick={exportToPDF} disabled={loading || planillaData.length === 0 || !isExportEnabled} />
                    <ButtonExcel onClick={exportToExcel} disabled={loading || planillaData.length === 0 || !isExportEnabled} />
                </div>
            </div>
            <div className="filtros">
                <div>
                    <label>Periodo de fecha:</label>
                    <input type="month" value={periodo_fecha} onChange={(e) => setPeriodoFecha(e.target.value)} />
                </div>
            </div>
            <div className="flex flex-col gap-4 mb-4">
                <div className="flex flex-col gap-3">
                    <div className="w-full">
                        <ButtonSearch onClick={handleSearch} isLoading={loading} />
                    </div>
                    <div className="flex justify-between items-center w-full">
                        <div className="flex items-center gap-4">
                            {planillaData.length > 0 && <span className="text-gray-700 font-medium text-xs font-bold">{getStatusText()}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                className={`px-4 py-2 rounded-lg text-xs font-semibold ${isApprovalDisabled() ? "bg-[#0b4d17] opacity-95 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"} text-white border-none`}
                                onClick={handleAprobar}
                                disabled={isApprovalDisabled()}
                            >
                                {userId === 11 ? "Guardar y Enviar" : "Aprobar y firmar"}
                            </Button>
                            {[11, 5, 22, 8, 26].includes(Number(state.user?.perfil_id)) && !planillaData.some(r => r.planilla_negocios_id !== null) && (
                                <ButtonInsert onClick={handleAddRow} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="table-container overflow-x-auto min-h-[400px]">
                <Table bordered responsive size="sm" className="text-[11px]">
                    <thead className="bg-[#14233a] text-white text-center align-middle">
                        <tr>
                            <th className="p-1" style={{ width: '40px' }}>N°</th>
                            <th className="p-1" style={{ width: '155px', minWidth: '155px', maxWidth: '155px' }}>AREA</th>
                            <th className="p-1 min-w-[320px]">APELLIDOS Y NOMBRES</th>
                            <th className="p-1 min-w-[150px]">CARGO</th>
                            <th className="p-1 min-w-[100px]">REMUNERACIÓN</th>
                            <th className="p-1 min-w-[110px]">BONO ANUAL</th>
                            <th className="p-1 min-w-[120px] bg-red-600">DESCUENTO POR<br />TARDANZA O FALTAS</th>
                            <th className="p-1 min-w-[130px]">TOTAL A PAGAR<br />PLANILLA</th>
                            <th className="p-1 min-w-[130px]">TOTAL APORT. DEL<br />TRABAJADOR</th>
                            <th className="p-1 bg-yellow-400 text-black min-w-[130px]">IMPORTE NETO<br />A PAGAR</th>
                            <th className="p-1 min-w-[90px]">N° DNI</th>
                            <th className="p-1 min-w-[170px]">N° CUENTA</th>
                            <th className="p-1 min-w-[150px]">BANCO</th>
                            <th className="p-1">BOLETA</th>
                            <th className="p-1 min-w-[130px] bg-[#E1D5E7] text-black">FECHA PAGO</th>
                        </tr>
                    </thead>
                    <tbody>
                        {planillaData.length > 0 ? planillaData.map((row, index) => (
                            <tr key={index} className="align-middle">
                                <td>{index + 1}</td>
                                <td>
                                    {!canEdit ? <span className="px-1">{row.area}</span> : <input type="text" value={row.area} onChange={(e) => handleInputChange(e, index, 'area')} className="form-control form-control-sm text-[11px] h-7" />}
                                </td>
                                <td>
                                    {!canEdit ? <span className="px-1">{row.apellidos_nombre}</span> : <input type="text" value={row.apellidos_nombre} onChange={(e) => handleInputChange(e, index, 'apellidos_nombre')} className="form-control form-control-sm text-[11px] h-7" />}
                                </td>
                                <td>
                                    {!canEdit ? <span className="px-1">{row.cargo}</span> : <input type="text" value={row.cargo} onChange={(e) => handleInputChange(e, index, 'cargo')} className="form-control form-control-sm text-[11px] h-7" />}
                                </td>
                                <td className="text-center">
                                    {!canEdit ? <span>{formatSoles(row.remuneracion)}</span> : (
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-xs text-gray-500">S/</span>
                                            <input type="number" step="0.01" value={row.remuneracion === 0 ? "0.00" : row.remuneracion || ''} onChange={(e) => handleInputChange(e, index, 'remuneracion')} className="form-control form-control-sm text-[11px] h-7 w-20 text-center" />
                                        </div>
                                    )}
                                </td>
                                <td className="text-center">
                                    {!canEdit ? <span>{formatSoles(row.bono_anual)}</span> : (
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-xs text-gray-500">S/</span>
                                            <input type="number" step="0.01" value={row.bono_anual === 0 ? "0.00" : row.bono_anual || ''} onChange={(e) => handleInputChange(e, index, 'bono_anual')} className="form-control form-control-sm text-[11px] h-7 w-20 text-center" />
                                        </div>
                                    )}
                                </td>
                                <td className="text-center bg-red-100">
                                    {!canEdit ? <span>{formatSoles(row.descuento_tardanza_faltas)}</span> : (
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-xs text-gray-500">S/</span>
                                            <input type="number" step="0.01" value={row.descuento_tardanza_faltas === 0 ? "0.00" : row.descuento_tardanza_faltas || ''} onChange={(e) => handleInputChange(e, index, 'descuento_tardanza_faltas')} className="form-control form-control-sm text-[11px] h-7 w-20 text-center" />
                                        </div>
                                    )}
                                </td>
                                <td className="font-bold text-center bg-gray-50">{formatSoles(row.total_pagar_planilla)}</td>
                                <td className="text-center">
                                    {!canEdit ? <span>{formatSoles(row.total_aporte_trabajador)}</span> : (
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-xs text-gray-500">S/</span>
                                            <input type="number" step="0.01" value={row.total_aporte_trabajador === 0 ? "0.00" : row.total_aporte_trabajador || ''} onChange={(e) => handleInputChange(e, index, 'total_aporte_trabajador')} className="form-control form-control-sm text-[11px] h-7 w-20 text-center" />
                                        </div>
                                    )}
                                </td>
                                <td className="bg-yellow-100 font-bold text-center">{formatSoles(row.importe_neto_pagar)}</td>
                                <td className="text-center">
                                    {!canEdit ? <span>{row.dni}</span> : <input type="text" value={row.dni} maxLength={8} onChange={(e) => handleInputChange(e, index, 'dni')} className="form-control form-control-sm text-[11px] h-7 w-20" />}
                                </td>
                                <td className="text-center">
                                    {!canEdit ? <span>{row.n_cuenta}</span> : <input type="text" value={row.n_cuenta} onChange={(e) => handleInputChange(e, index, 'n_cuenta')} className="form-control form-control-sm text-[11px] h-7 w-40" />}
                                </td>
                                <td className="text-center">
                                    {!canEdit ? <span>{row.banco}</span> : <input type="text" value={row.banco} onChange={(e) => handleInputChange(e, index, 'banco')} className="form-control form-control-sm text-[11px] h-7 w-32" />}
                                </td>
                                <td className="text-center">
                                    <button
                                        onClick={() => {
                                            if (row.boleta_url) window.open(row.boleta_url, '_blank');
                                            else if (canUploadBoleta && row.planilla_negocios_id) {
                                                const input = document.createElement('input');
                                                input.type = 'file'; input.accept = 'application/pdf';
                                                input.onchange = async (e) => {
                                                    const file = e.target.files[0];
                                                    if (!file) return;
                                                    if (file.size > 5 * 1024 * 1024) {
                                                        alert("El archivo excede el límite de 5MB. Por favor suba un archivo más pequeño.");
                                                        return;
                                                    }
                                                    if (!window.confirm(`¿Subir boleta para ${row.apellidos_nombre}?`)) return;
                                                    setLoading(true);
                                                    try {
                                                        await ArchivosService.guardarBoletaPago(file, row.planilla_negocios_id, periodo_fecha, state.user.usuario_id);
                                                        fetchDatos();
                                                    } finally { setLoading(false); }
                                                };
                                                input.click();
                                            } else {
                                                alert("Debe guardar el registro antes de subir la boleta.");
                                            }
                                        }}
                                        className="p-1 hover:bg-gray-100 rounded"
                                    >
                                        <FileMinus size={18} style={{ color: row.boleta_url ? '#3cb167' : '#f87171' }} />
                                    </button>
                                </td>
                                 <td>
                                    {(canEditPaymentDate && !row.fecha_pago_db) ? (
                                        <input
                                            type="date"
                                            value={row.fecha_pago || ''}
                                            onChange={async (e) => {
                                                const newVal = e.target.value;
                                                if (!newVal || !window.confirm(`¿Desea registrar ${newVal} como FECHA DE PAGO para ${row.apellidos_nombre}?`)) return;
                                                setLoading(true);
                                                try {
                                                    const savePayload = {
                                                        planilla_negocios_id: row.planilla_negocios_id,
                                                        area: row.area,
                                                        apellidos_nombre: row.apellidos_nombre,
                                                        cargo: row.cargo,
                                                        remuneracion: row.remuneracion,
                                                        bono_anual: row.bono_anual,
                                                        descuento_tardanza_faltas: row.descuento_tardanza_faltas,
                                                        total_pagar_planilla: row.total_pagar_planilla,
                                                        total_aporte_trabajador: row.total_aporte_trabajador,
                                                        importe_neto_pagar: row.importe_neto_pagar,
                                                        dni: row.dni,
                                                        n_cuenta: row.n_cuenta,
                                                        banco: row.banco,
                                                        fecha_pago: newVal
                                                    };
                                                    await PlanillaNegociosService.guardarPlanilla({
                                                        periodo_planilla: periodo_fecha,
                                                        registros: [savePayload]
                                                    });
                                                    alert("Fecha de pago registrada correctamente.");
                                                    fetchDatos();
                                                } catch (error) {
                                                    alert("Error al registrar fecha: " + (error.response?.data?.error || error.message));
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                            className="form-control form-control-sm h-7 text-[11px]"
                                        />
                                    ) : (<span>{row.fecha_pago || '—'}</span>)}
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="15" className="text-center py-4 bg-white text-gray-400">No hay registros. Haga clic en Agregar para iniciar.</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="font-bold border-t-2">
                            <td colSpan="9" className="text-right p-1"></td>
                            <td className="bg-yellow-200 p-1 text-center">{formatSoles(totalImporteNeto)}</td>
                            <td colSpan="5"></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
}

export default PlanillaNegocios;
